/**
 * 🙈 BLIND DATE TAB CONTAINER - Architecture Mozart/Jobs/Tesla
 * 
 * Conteneur pour l'onglet Blind Date dans Heartbeats.
 */

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Eye, EyeOff, MessageCircle, Sparkles, Users } from 'lucide-react';
import { Button } from '../../ui/button';
import { useBlindDate } from '../../../hooks/heartbeats';
import { BlindDateCard, BlindDatePanel, RevealMoment } from '../features/blind-date';
import { GuideAvatar } from '../soul/GuideAvatar';

export const BlindDateTab = ({ customerId, isDarkMode }) => {
  const blindDate = useBlindDate(customerId);
  const [showIntro, setShowIntro] = useState(true);
  const [selectedProfile, setSelectedProfile] = useState(null);

  useEffect(() => {
    blindDate.loadBlindProfiles();
    blindDate.loadMySessions();
  }, []);

  const handleStartBlindDate = async (targetId) => {
    const session = await blindDate.startBlindDate(targetId);
    if (session) {
      setSelectedProfile(null);
    }
  };

  return (
    <div className={`p-4 space-y-6 ${isDarkMode ? 'bg-gray-900' : 'bg-white'}`}>
      {/* Intro */}
      {showIntro && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className={`p-6 rounded-2xl text-center ${
            isDarkMode 
              ? 'bg-gradient-to-br from-purple-900/50 to-pink-900/50 border border-purple-700/30' 
              : 'bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200'
          }`}
        >
          <div className="mb-4 flex justify-center">
            <GuideAvatar guide="pixie" expression="wink" size="lg" />
          </div>
          <h2 className={`text-xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Blind Date Mode
          </h2>
          <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Découvre la personnalité avant le physique !<br/>
            Les photos sont cachées jusqu'à ce que vous échangiez <strong>{blindDate.MESSAGES_TO_REVEAL} messages</strong>.
          </p>
          
          <div className={`p-4 rounded-xl mb-4 ${
            isDarkMode ? 'bg-black/30' : 'bg-white/70'
          }`}>
            <p className={`text-sm font-medium ${isDarkMode ? 'text-purple-300' : 'text-purple-600'}`}>
              💡 Pourquoi c'est génial ?
            </p>
            <ul className={`text-xs mt-2 space-y-1 text-left ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              <li>• Finis les jugements superficiels</li>
              <li>• Connexion authentique d'abord</li>
              <li>• Surprise garantie au reveal !</li>
              <li>• Plus de conversations profondes</li>
            </ul>
          </div>

          <Button
            onClick={() => setShowIntro(false)}
            className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
          >
            💜 C'est parti !
          </Button>
        </motion.div>
      )}

      {/* Active Sessions */}
      {!showIntro && blindDate.activeSessions.length > 0 && (
        <div>
          <h3 className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            💬 Tes Blind Dates en cours
          </h3>
          <div className="space-y-3">
            {blindDate.activeSessions.map(session => (
              <BlindDateCard
                key={session.id}
                session={session}
                isDarkMode={isDarkMode}
                onClick={() => blindDate.openSession(session.id)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Blind Profiles Grid */}
      {!showIntro && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h3 className={`text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              <EyeOff className="inline w-4 h-4 mr-1" />
              Profils mystères
            </h3>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => blindDate.loadBlindProfiles()}
              disabled={blindDate.isLoading}
            >
              🔄 Charger les profils
            </Button>
          </div>
          
          {blindDate.blindProfiles.length === 0 ? (
            <div className={`text-center py-8 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              <div className="text-4xl mb-2">👻</div>
              <p>Aucun profil disponible pour le moment</p>
              <p className="text-xs mt-1">Reviens plus tard !</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              {blindDate.blindProfiles.map(profile => (
                <motion.div
                  key={profile.id}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={`p-4 rounded-xl cursor-pointer transition-all ${
                    isDarkMode 
                      ? 'bg-gray-800/50 border border-gray-700 hover:border-purple-500/50' 
                      : 'bg-white border border-gray-200 hover:border-purple-300 shadow-sm'
                  }`}
                  onClick={() => setSelectedProfile(profile)}
                >
                  {/* Hidden Avatar - Pixie mystère */}
                  <div className={`w-16 h-16 mx-auto rounded-full mb-3 flex items-center justify-center ${
                    isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                  }`}>
                    <GuideAvatar guide="pixie" expression="default" size="sm" />
                  </div>
                  
                  {/* Name & Age */}
                  <p className={`font-medium text-center ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {profile.displayName}, {profile.age}
                  </p>
                  
                  {/* Tagline */}
                  <p className={`text-xs text-center mt-1 line-clamp-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {profile.tagline || profile.bio?.substring(0, 50) || '✨ À découvrir...'}
                  </p>
                  
                  {/* Passions preview */}
                  {profile.passionsPreview && (
                    <div className="flex flex-wrap justify-center gap-1 mt-2">
                      {profile.passionsPreview.slice(0, 3).map((p, i) => (
                        <span key={i} className="text-xs">{p}</span>
                      ))}
                    </div>
                  )}
                </motion.div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Profile Detail Modal */}
      {selectedProfile && (
        <BlindDatePanel
          profile={selectedProfile}
          isDarkMode={isDarkMode}
          onStart={() => handleStartBlindDate(selectedProfile.id)}
          onClose={() => setSelectedProfile(null)}
          isLoading={blindDate.isLoading}
        />
      )}

      {/* Active Chat Session */}
      {blindDate.currentSession && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className={`w-full max-w-lg rounded-2xl overflow-hidden ${
              isDarkMode ? 'bg-gray-900' : 'bg-white'
            }`}
          >
            {/* Chat Header */}
            <div className={`p-4 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500 flex items-center justify-center text-white">
                    {blindDate.currentSession.isRevealed ? (
                      <img src={blindDate.currentSession.partnerPhoto} alt="" className="w-full h-full rounded-full object-cover" />
                    ) : (
                      <GuideAvatar guide="pixie" expression="default" size="xs" />
                    )}
                  </div>
                  <div>
                    <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {blindDate.currentSession.partnerName}
                    </p>
                    <p className="text-xs text-purple-400">
                      {blindDate.revealReady ? '🌟 Photos visibles !' : `${blindDate.messages.length}/${blindDate.MESSAGES_TO_REVEAL} messages`}
                    </p>
                  </div>
                </div>
                <Button variant="ghost" size="sm" onClick={blindDate.closeSession}>
                  ✕
                </Button>
              </div>
              
              {/* Progress bar */}
              {!blindDate.revealReady && (
                <div className="mt-3 h-2 rounded-full bg-gray-200 overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300"
                    style={{ width: `${(blindDate.messages.length / blindDate.MESSAGES_TO_REVEAL) * 100}%` }}
                  />
                </div>
              )}
            </div>
            
            {/* Messages would go here - simplified for now */}
            <div className="h-80 p-4 overflow-y-auto">
              {blindDate.messages.map((msg, i) => (
                <div key={i} className={`mb-2 ${msg.senderId === customerId ? 'text-right' : 'text-left'}`}>
                  <span className={`inline-block px-3 py-2 rounded-xl text-sm ${
                    msg.senderId === customerId 
                      ? 'bg-purple-500 text-white' 
                      : isDarkMode ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-900'
                  }`}>
                    {msg.text}
                  </span>
                </div>
              ))}
            </div>
            
            {/* Reveal Button */}
            {blindDate.revealReady && !blindDate.currentSession.isRevealed && (
              <div className="p-4 border-t border-purple-500/30 bg-purple-500/10 text-center">
                <Button
                  onClick={blindDate.revealPhotos}
                  disabled={blindDate.isLoading}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 text-white"
                >
                  🌟 Voir les photos !
                </Button>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default BlindDateTab;
